import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Paper,
} from '@mui/material';
import {
  Event as EventIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarMonthIcon,
  CloudOff as CloudOffIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import offlinePaymentService from '../../services/offlinePaymentService';

const CollectorHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [stats, setStats] = useState({
    dueToday: 0,
    overdue: 0,
    totalPayable: 0,
    totalPayableCount: 0,
    collectedToday: 0,
    collectedTodayCount: 0,
    pendingOffline: 0,
    activeLoans: 0,
    collectionRate: 0,
    averagePerInstallment: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    fetchDashboardStats();
    
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      fetchDashboardStats();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (navigator.onLine) {
        fetchDashboardStats();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const fetchDashboardStats = async () => {
    if (!navigator.onLine) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch dashboard stats from dedicated endpoint (all metrics filtered by collector)
      const [statsRes, pendingOfflineRes] = await Promise.allSettled([
        api.get(`/collector/dashboard/stats?collectorId=${user.userId}`),
        offlinePaymentService.getPendingPaymentsCount(),
      ]);

      // Parse dashboard stats response
      const statsData = statsRes.status === 'fulfilled' ? statsRes.value.data : null;
      const pendingOffline = pendingOfflineRes.status === 'fulfilled' ? pendingOfflineRes.value : 0;

      if (statsData) {
        setStats({
          dueToday: statsData.dueToday || 0,
          overdue: statsData.overdue || 0,
          totalPayable: parseFloat(statsData.totalPayable) || 0,
          totalPayableCount: statsData.totalPayableCount || 0,
          collectedToday: parseFloat(statsData.collectedToday) || 0,
          collectedTodayCount: statsData.collectedTodayCount || 0,
          pendingOffline: pendingOffline || 0,
          activeLoans: statsData.activeLoans || 0,
          collectionRate: parseFloat(statsData.collectionRate) || 0,
          averagePerInstallment: parseFloat(statsData.averagePerInstallment) || 0,
        });
      } else {
        // Fallback: set default values
        setStats({
          dueToday: 0,
          overdue: 0,
          totalPayable: 0,
          totalPayableCount: 0,
          collectedToday: 0,
          collectedTodayCount: 0,
          pendingOffline: pendingOffline || 0,
          activeLoans: 0,
          collectionRate: 0,
          averagePerInstallment: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Set default values on error
      setStats({
        dueToday: 0,
        overdue: 0,
        totalPayable: 0,
        totalPayableCount: 0,
        collectedToday: 0,
        collectedTodayCount: 0,
        pendingOffline: 0,
        activeLoans: 0,
        collectionRate: 0,
        averagePerInstallment: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const StatCard = ({ title, value, icon, color, gradient, onClick, subtitle }) => (
    <Card
      onClick={onClick}
      sx={{
        background: gradient || `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: 'white',
        boxShadow: `0 4px 20px ${color}40`,
        borderRadius: 3,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 30px ${color}60`,
        } : {},
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: 2, 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
          {onClick && (
            <ArrowForwardIcon sx={{ opacity: 0.7, fontSize: { xs: 20, sm: 24 } }} />
          )}
        </Box>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 0.5,
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
          }}
        >
          {loading ? '...' : value}
      </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            opacity: 0.9,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 500,
          }}
        >
          {title}
      </Typography>
        {subtitle && (
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.8,
              display: 'block',
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon, onClick, color }) => (
    <Card
      onClick={onClick}
      sx={{
        border: `2px solid ${color}`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        height: '100%',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 20px ${color}40`,
          bgcolor: `${color}08`,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          {description}
          </Typography>
        </CardContent>
      </Card>
  );

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
          }}
        >
          Field Officer Dashboard
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          Welcome back, {user?.fullName || 'Field Officer'}. Here's your overview.
        </Typography>
      </Box>

      {/* Offline Alert */}
      {isOffline && (
        <Alert 
          severity="warning" 
          icon={<CloudOffIcon />}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
            },
          }}
        >
          You are currently offline. Some features may be limited.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Due"
                value={formatCurrency(stats.totalPayable)}
                icon={<EventIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />}
                color="#1976D2"
                gradient="linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)"
                onClick={() => navigate('/collector/collections')}
                subtitle={stats.totalPayableCount > 0 ? `${stats.totalPayableCount} installment(s)` : stats.dueToday > 0 ? `${stats.dueToday} installment(s) due today` : 'No installments due'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Overdue"
                value={stats.overdue}
                icon={<WarningIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />}
                color="#D32F2F"
                gradient="linear-gradient(135deg, #EF5350 0%, #D32F2F 100%)"
                onClick={() => navigate('/collector/overdue')}
                subtitle="Installments past due date"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Collected Today"
                value={formatCurrency(stats.collectedToday)}
                icon={<AttachMoneyIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />}
                color="#2E7D32"
                gradient="linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)"
                onClick={() => navigate('/collector/collected-installments')}
                subtitle="Total collections for today"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Pending Sync"
                value={stats.pendingOffline}
                icon={<CloudOffIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />}
                color="#F57C00"
                gradient="linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
                subtitle="Offline payments waiting to sync"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Active Loans"
                value={stats.activeLoans}
                icon={<AccountBalanceIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />}
                color="#7B1FA2"
                gradient="linear-gradient(135deg, #BA68C8 0%, #7B1FA2 100%)"
                onClick={() => navigate('/collector/loan-schedules')}
                subtitle="Loans currently active"
            />
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
              mb: { xs: 3, sm: 4 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon sx={{ color: '#0D47A1', mr: 1, fontSize: { xs: 24, sm: 28 } }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: '#0D47A1',
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
              >
                Quick Actions
                  </Typography>
            </Box>
            <Grid container spacing={{ xs: 2, sm: 2.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Record Payment"
                  description="Mark installments as paid and record collections"
                  icon={<AttachMoneyIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />}
                  onClick={() => navigate('/collector/loan-schedules')}
                  color="#1976D2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="View Collections"
                  description="See your collection history and reports"
                  icon={<CalendarMonthIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />}
                  onClick={() => navigate('/collector/collections')}
                  color="#2E7D32"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <QuickActionCard
                  title="Overdue Installments"
                  description="Check and manage overdue payments"
                  icon={<WarningIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />}
                  onClick={() => navigate('/collector/overdue')}
                  color="#D32F2F"
                />
          </Grid>
        </Grid>
      </Paper>

          {/* Performance Summary */}
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(13, 71, 161, 0.05) 100%)',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: '#0D47A1',
                mb: 2,
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
              }}
            >
              Today's Performance
          </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'white', borderRadius: 2 }}>
            <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Collection Rate
                        </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2E7D32', fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                      {stats.dueToday > 0 
                        ? `${Math.round(stats.collectionRate)}%`
                        : '0%'}
                        </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ color: '#2E7D32', fontSize: { xs: 32, sm: 40 } }} />
                </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Average per Installment
                        </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976D2', fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                      {stats.collectedTodayCount > 0 
                        ? formatCurrency(stats.averagePerInstallment)
                        : formatCurrency(0)}
                        </Typography>
                  </Box>
                  <AttachMoneyIcon sx={{ color: '#1976D2', fontSize: { xs: 32, sm: 40 } }} />
                </Box>
                      </Grid>
                    </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CollectorHome;
